import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import type { PropsWithChildren, Ref } from 'react';
import { lazy, Suspense, useMemo, useState } from 'react';
import type { MapProps, MapRef } from 'react-map-gl';

import { useMapTransformRequest } from './useMapTransformRequest';
import { useAppSelector } from 'src/app/store/hooks';
import type { MapStyleName } from 'src/common/external/aws/mapsV2';
import { buildMapStyleUrl } from 'src/common/external/aws/mapsV2';
import { selectCurrentOrganizationHeadquartersLocation } from 'src/features/auth/selectors';

// The map route and trip summary are rarely the user's first destination, and
// react-map-gl + maplibre-gl are hefty. Keep them out of the initial chunk by
// lazy-loading the map components; they resolve inside the Suspense boundary
// below. `Map` is lazy, but consumers (e.g. CaptureMapAsImage) still pass a ref
// through mapViewProps — React forwards a ref placed in a spread to a lazy
// component, so MapViewProps must include it (MapProps alone omits ref;
// ComponentProps used to resolve it implicitly).
const Map = lazy(() => import('react-map-gl'));
const NavigationControl = lazy(() =>
  import('react-map-gl').then((module) => ({
    default: module.NavigationControl,
  }))
);

export const BAMBI_MAPVIEW_DEFAULT_ZOOM_DURATION = 300;

export type MapViewProps = MapProps & { ref?: Ref<MapRef> };

type BambiMapViewProps = PropsWithChildren & {
  mapViewProps?: MapViewProps;
  styleName?: MapStyleName;
  traffic?: boolean;
};

export const DEFAULT_ZOOM_LEVEL = 9;

/**
 * Be sure to pass initial properties, like zoom and lat/long, inside
 * the mapViewProps.initialViewState object and not at the top level.
 */
export function BambiMapView({
  children,
  mapViewProps = {},
  styleName = 'Standard',
  traffic = false,
}: BambiMapViewProps) {
  const hqLocation = useAppSelector(
    selectCurrentOrganizationHeadquartersLocation
  );

  const [currentZoomLevelForTests, setCurrentZoomLevelForTests] =
    useState(DEFAULT_ZOOM_LEVEL);
  return (
    <>
      <div
        data-initial-address={hqLocation?.address}
        data-testid="current-zoom-level-for-tests"
        style={{ display: 'none' }}
      >
        {currentZoomLevelForTests}
      </div>
      <SuspendedMapView
        styleName={styleName}
        traffic={traffic}
        mapViewProps={{
          ...mapViewProps,
          initialViewState: {
            zoom: DEFAULT_ZOOM_LEVEL,
            latitude: hqLocation?.latitude,
            longitude: hqLocation?.longitude,
            ...mapViewProps.initialViewState, // Allow parent to override
            fitBoundsOptions: {
              duration: BAMBI_MAPVIEW_DEFAULT_ZOOM_DURATION,
              ...mapViewProps.initialViewState?.fitBoundsOptions, // Allow parent to override
            },
          },
          onZoom: (e) => setCurrentZoomLevelForTests(e.viewState.zoom),
        }}
      >
        {children}
      </SuspendedMapView>
    </>
  );
}

interface SuspendedMapViewProps extends PropsWithChildren {
  mapViewProps: MapViewProps;
  includeNavigationControl?: boolean;
  styleName?: MapStyleName;
  traffic?: boolean;
}

export function SuspendedMapView({
  mapViewProps,
  includeNavigationControl = true,
  styleName = 'Standard',
  traffic = false,
  children,
}: SuspendedMapViewProps) {
  const { transformRequest, error, retry } = useMapTransformRequest();
  const mapStyle = buildMapStyleUrl({ style: styleName, traffic });
  const mapLib = useMemo(() => import('maplibre-gl'), []);

  if (error) {
    return (
      <div
        data-testid="map-load-error"
        className="flex h-full w-full flex-col items-center justify-center gap-3 text-gray-700"
      >
        <p>Unable to load the map.</p>
        <Button variant="secondary-outline" onClick={retry}>
          Try again
        </Button>
      </div>
    );
  }

  if (!transformRequest) {
    return <LoadingIndicator />;
  }

  return (
    <Suspense fallback={<LoadingIndicator />}>
      <style>{
        // The maplibre engine emits both `.maplibregl-map` and `.mapboxgl-map`
        // classes; these size/clean the rendered map.
        `
        .maplibregl-map.mapboxgl-map {
          height: 100% !important;
          width: 100% !important;
        }
        .mapboxgl-popup-close-button {
          display: none;
        }
    `
      }</style>
      <Map
        {...mapViewProps}
        mapLib={mapLib}
        mapStyle={mapStyle}
        transformRequest={transformRequest}
      >
        {includeNavigationControl ? (
          <NavigationControl
            position="bottom-right"
            showZoom
            showCompass={false}
          />
        ) : null}
        {children}
      </Map>
    </Suspense>
  );
}
